import React from 'react';

/**
 * Get color for mastery level
 */
function getMasteryColor(level) {
  switch (level?.toLowerCase()) {
    case 'mastered':
      return 'var(--success)';
    case 'proficient':
      return 'var(--accent-primary)';
    case 'developing':
    case 'emerging':
      return 'var(--warning)';
    case 'beginning':
    default:
      return 'var(--text-tertiary)';
  }
}

/**
 * CourseMasteryProgress - Compact horizontal mastery progress for course pages
 */
export function CourseMasteryProgress({
  mastery = 0,
  masteryLevel = 'Beginning',
  confidence = 0,
  dueForReview = false,
}) {
  const percentage = Math.round(mastery * 100);
  const color = getMasteryColor(masteryLevel);
  const confidenceLabel = confidence >= 0.7 ? 'High' : confidence >= 0.4 ? 'Med' : 'Low';

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-3)',
      padding: 'var(--space-3) var(--space-4)',
      background: 'var(--glass-bg-light)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 'var(--radius-control)',
    }}>
      {/* Brain icon */}
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--text-tertiary)"
        strokeWidth="2"
        style={{ flexShrink: 0 }}
      >
        <path d="M12 4.5a2.5 2.5 0 0 0-4.96-.46 2.5 2.5 0 0 0-1.98 3 2.5 2.5 0 0 0-1.32 4.24 3 3 0 0 0 .34 5.58 2.5 2.5 0 0 0 2.96 3.08A2.5 2.5 0 0 0 12 19.5a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 12 4.5" />
      </svg>

      {/* Mastery info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-1)',
        }}>
          <span style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--text-secondary)',
          }}>
            Mastery
          </span>
          <span style={{
            fontSize: 11,
            fontWeight: 600,
            color: color,
          }}>
            {percentage}% {masteryLevel}
          </span>
        </div>

        {/* Progress bar */}
        <div style={{
          height: 4,
          borderRadius: 'var(--radius-full)',
          background: 'var(--bg-tertiary)',
          overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            width: `${percentage}%`,
            background: color,
            borderRadius: 'var(--radius-full)',
            transition: 'width 0.3s ease',
          }} />
        </div>
      </div>

      {/* Confidence badge */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--space-1)',
      }}>
        <span style={{
          fontSize: 'var(--text-label-size)',
          fontWeight: 500,
          color: 'var(--text-tertiary)',
          textTransform: 'uppercase',
        }}>
          Conf
        </span>
        <span style={{
          fontSize: 'var(--text-label-size)',
          fontWeight: 600,
          padding: 'var(--space-1) var(--space-2)',
          borderRadius: 'var(--radius-control)',
          background: 'var(--glass-bg-medium)',
          color: 'var(--text-secondary)',
        }}>
          {confidenceLabel}
        </span>
      </div>

      {/* Review indicator */}
      {dueForReview && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-1)',
          padding: 'var(--space-1) var(--space-2)',
          borderRadius: 'var(--radius-control)',
          background: 'var(--warning-bg)',
          color: 'var(--warning)',
          fontSize: 'var(--text-label-size)',
          fontWeight: 600,
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Review
        </div>
      )}
    </div>
  );
}
